import React from 'react';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Cpu, Users, Cog, Award, ShoppingBag, X, Plus } from 'lucide-react';
import { formatPrice } from '../lib/currency';
import InquiryForm from '../components/InquiryForm';
import ProductCard, { Product, Category } from '../components/ProductCard';
import ProductImageGallery from '../components/ProductImageGallery';
import ProductEditForm from '../components/ProductEditForm';
import ParticleBackground from '../components/ParticleBackground';
import RoboticsSectionsEditor from '../components/RoboticsSectionsEditor';
import { useSupabaseProducts } from '../hooks/useSupabaseProducts';
import { useRoboticsSections } from '../hooks/useRoboticsSections';

export default function RoboticsPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showSectionsEditor, setShowSectionsEditor] = useState(false);

  const isAdmin = new URLSearchParams(location.search).get('admin') === 'true';

  const {
    products,
    categories,
    loading,
    error,
    addProduct,
    editProduct: updateProduct,
    removeProduct,
    addCategory,
    editCategory,
    removeCategory
  } = useSupabaseProducts('robotics');

  const {
    sections: roboticsSections,
    updateSections: updateRoboticsSections,
    loading: sectionsLoading
  } = useRoboticsSections();

  // Save current section to sessionStorage for navigation
  useEffect(() => {
    sessionStorage.setItem('currentSection', 'robotics');
  }, []);

  // Cleanup scroll on unmount or when all modals are closed
  useEffect(() => {
    return () => {
      // Always restore scroll when component unmounts
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Reset scroll if all modals are closed
  useEffect(() => {
    if (!showProductDetails && !showEditModal && !showCategoryManager && !showSectionsEditor) {
      document.body.style.overflow = 'unset';
    }
  }, [showProductDetails, showEditModal, showCategoryManager, showSectionsEditor]);

  const handleEditProduct = async (updatedProduct: Product) => {
    await updateProduct(updatedProduct);
    setShowEditModal(false);
    setEditingProduct(null);
  };

  const handleDeleteProduct = (id: string) => {
    if (confirm('Opravdu chcete smazat tento produkt?')) {
      removeProduct(id).catch(err => {
        console.error('Error deleting product:', err);
      });
    }
  };

  const handleSaveCategories = (updatedCategories: Category[]) => {
    console.log('Categories updated:', updatedCategories);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newProducts = [...products];
    const draggedProduct = newProducts[draggedIndex];
    newProducts.splice(draggedIndex, 1);
    newProducts.splice(dropIndex, 0, draggedProduct);

    try {
      // Update order_index for all affected products
      const updatePromises = newProducts.map((product, index) =>
        updateProduct({
          ...product,
          order: index
        })
      );

      await Promise.all(updatePromises);
    } catch (err) {
      console.error('Error updating product order:', err);
    }

    setDraggedIndex(null);
  };

  const handleShowDetails = (product: Product) => {
    setSelectedProduct(product);
    setShowProductDetails(true);
    // Lock scroll and hide navbar
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new Event('modalOpen'));
  };

  const handleEditClick = (product: Product) => {
    setEditingProduct(product);
    setShowEditModal(true);
    // Lock scroll and hide navbar
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new Event('modalOpen'));
  };

  const closeProductDetails = () => {
    setShowProductDetails(false);
    // Unlock scroll and show navbar
    document.body.style.overflow = 'unset';
    window.dispatchEvent(new Event('modalClose'));
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingProduct(null);
    // Unlock scroll and show navbar
    document.body.style.overflow = 'unset';
    window.dispatchEvent(new Event('modalClose'));
  };

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(product => product.categories?.includes(selectedCategory));

  const handleSectionClick = (index: number) => {
    switch(index) {
      case 0:
        const productsSection = document.getElementById('products-section');
        if (productsSection) {
          productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        break;
      case 1:
        navigate('/neseriove-vyrobky');
        break;
      case 2:
        navigate('/reference');
        break;
    }
  };

  const currentLang = i18n.language as 'cs' | 'en' | 'de' | 'sk';

  const sections = [
    {
      icon: <Cog className="w-12 h-12" />,
      title: roboticsSections.products.title[currentLang] || roboticsSections.products.title.cs,
      description: roboticsSections.products.description[currentLang] || roboticsSections.products.description.cs,
      clickable: true
    },
    {
      icon: <Award className="w-12 h-12" />,
      title: roboticsSections.services.title[currentLang] || roboticsSections.services.title.cs,
      description: roboticsSections.services.description[currentLang] || roboticsSections.services.description.cs,
      clickable: true
    },
    {
      icon: <Cpu className="w-12 h-12" />,
      title: roboticsSections.references.title[currentLang] || roboticsSections.references.title.cs,
      description: roboticsSections.references.description[currentLang] || roboticsSections.references.description.cs,
      clickable: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-20">
      <ParticleBackground />
      {/* Hero Section */}
      <section className="relative py-20 px-4 z-10">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center justify-center mb-6">
              <Cpu className="w-16 h-16 text-blue-500 mr-4" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 font-orbitron">
                {t('robotics.title')}
              </h1>
            </div>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              {t('robotics.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          {isAdmin && (
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setShowSectionsEditor(true)}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Upravit texty
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onClick={() => handleSectionClick(index)}
                className="bg-black/40 backdrop-blur-md border border-blue-500/20 rounded-2xl p-8 text-center hover:border-blue-400/40 transition-all hover:scale-105 cursor-pointer relative group"
              >
                <div className="text-blue-500 mb-6 flex justify-center">
                  {section.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-4 font-orbitron">
                  {section.title}
                </h3>
                <p className="text-gray-400">
                  {section.description}
                </p>
                {index === 1 && (
                  <p className="text-blue-400 text-sm mt-4 font-medium">Zákaznický vývoj →</p>
                )}
                {index === 2 && (
                  <p className="text-blue-400 text-sm mt-4 font-medium">Zobrazit reference →</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products-section" className="py-20 px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center mb-6">
              <ShoppingBag className="w-16 h-16 text-blue-500 mr-4" />
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 font-orbitron">
                {t('navigation.products')}
              </h2>
            </div>
          </motion.div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {t('common.all')}
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
                style={{
                  backgroundColor: selectedCategory === category.id ? category.color : undefined
                }}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {filteredProducts.map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                index={index}
                isAdmin={isAdmin}
                onEdit={handleEditClick}
                onDelete={handleDeleteProduct}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                language={currentLang}
                categories={categories}
                onShowDetails={handleShowDetails}
                sectionColor="blue"
              />
            ))}
          </div>

          {/* Admin Add Product Button */}
          {isAdmin && (
            <div className="flex justify-center items-center gap-4 mt-8">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={async () => {
                  const newProduct: Product = {
                    id: '',
                    name: { cs: 'Nový produkt', en: 'New product', de: 'Neues Produkt', sk: 'Nový produkt' },
                    description: { cs: '', en: '', de: '', sk: '' },
                    price: 1000,
                    currency: 'CZK',
                    category: 'robotics',
                    image: '/-ct_logo_light copy copy.png',
                    inStock: true,
                    featured: false,
                    discount: 0,
                    isOnSale: false,
                    isSoldOut: false,
                    isRecommended: false,
                    order: 0
                  };
                  try {
                    await addProduct(newProduct);
                  } catch (err) {
                    console.error('Error adding product:', err);
                  }
                }}
                className="bg-green-600 hover:bg-green-500 text-white px-8 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2"
              >
                <span className="text-xl">+</span>
                <span>{t('common.addNewProduct')}</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCategoryManager(true)}
                className="bg-purple-600 hover:bg-purple-500 text-white px-8 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2"
              >
                <span>{t('common.manageCategories')}</span>
              </motion.button>
            </div>
          )}
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full mb-4"></div>
          <p className="text-gray-400 text-sm">Načítání produktů…</p>
        </div>
      )}

      {/* Product Details Modal */}
      {showProductDetails && selectedProduct && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeProductDetails}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-gray-900 border border-blue-500/30 rounded-2xl p-6 w-[90vw] max-w-2xl max-h-[90vh] overflow-y-auto relative z-[60]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-white">{selectedProduct.name[currentLang] || selectedProduct.name.cs}</h3>
              <button
                onClick={closeProductDetails}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="aspect-video bg-gray-700 rounded-lg mb-3 overflow-hidden">
              <ProductImageGallery product={selectedProduct} />
            </div>
            
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-1">{t('shop.description')}:</h4>
                <p className="text-gray-400 text-sm">
                  {selectedProduct.description[currentLang] || selectedProduct.description.cs || 'Popis není k dispozici'}
                </p>
              </div>
              
              {/* Manual download */}
              {selectedProduct.manual && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-1">{t('shop.manual')}:</h4>
                  <a
                    href={selectedProduct.manual}
                    download
                    className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 rounded-lg transition-colors text-sm"
                  >
                    <span>📄</span>
                    <span>{t('shop.downloadManual')}</span>
                  </a>
                </div>
              )}
              
              {/* Video tutorial */}
              {selectedProduct.videoTutorial && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-1">{t('shop.videoTutorial')}:</h4>
                  <a
                    href={selectedProduct.videoTutorial}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-red-600 hover:bg-red-500 text-white px-3 py-2 rounded-lg transition-colors text-sm"
                  >
                    <span>🎥</span>
                    <span>{t('shop.playVideo')}</span>
                  </a>
                </div>
              )}
              
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-1">{t('shop.price')}:</h4>
                <div className="flex items-center space-x-3">
                  {selectedProduct.discount && selectedProduct.discount > 0 ? (
                    <>
                      <span className="text-gray-400 line-through text-sm">
                        {formatPrice(selectedProduct.price, i18n.language)}
                      </span>
                      <span className="text-blue-500 font-bold text-base">
                        {formatPrice(selectedProduct.price * (1 - selectedProduct.discount / 100), i18n.language)}
                      </span>
                      <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                        -{selectedProduct.discount}%
                      </span>
                    </>
                  ) : (
                    <span className="text-blue-500 font-bold text-base">
                      {formatPrice(selectedProduct.price, i18n.language)}
                    </span>
                  )}
                </div>
              </div>
              
              {selectedProduct.categories && selectedProduct.categories.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-1">{t('shop.categories')}:</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProduct.categories.map((categoryId) => {
                      const category = categories.find(c => c.id === categoryId);
                      return category ? (
                        <span
                          key={categoryId} 
                          className="px-4 py-2 rounded-full text-sm font-bold text-white"
                          style={{backgroundColor: category.color}}
                        >
                          {category.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              
              <div className="flex space-x-4 pt-2">
                <button
                  onClick={async () => {
                    if (!selectedProduct.isSoldOut) {
                      // Close modal first
                      closeProductDetails();

                      // Add to cart with animation
                      const cart = JSON.parse(localStorage.getItem('cart') || '{}');
                      const currentQuantity = cart[selectedProduct.id] || 0;
                      cart[selectedProduct.id] = currentQuantity + 1;
                      localStorage.setItem('cart', JSON.stringify(cart));

                      // Store category
                      const cartCategories = JSON.parse(localStorage.getItem('cart_categories') || '{}');
                      cartCategories[selectedProduct.id] = selectedProduct.category;
                      localStorage.setItem('cart_categories', JSON.stringify(cartCategories));

                      // Dispatch cart update events
                      window.dispatchEvent(new Event('cartUpdated'));
                      window.dispatchEvent(new Event('globalCartUpdate'));

                      // Create floating animation
                      const centerX = window.innerWidth / 2;
                      const centerY = window.innerHeight / 2;

                      const floatingElement = document.createElement('div');
                      floatingElement.innerHTML = `
                        <div style="
                          width: 60px;
                          height: 60px;
                          background-image: url('${selectedProduct.image}');
                          background-size: cover;
                          background-position: center;
                          border-radius: 12px;
                          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
                          border: 3px solid #22c55e;
                        "></div>
                      `;

                      floatingElement.style.cssText = `
                        position: fixed;
                        left: ${centerX - 30}px;
                        top: ${centerY - 30}px;
                        z-index: 9999;
                        pointer-events: none;
                        transition: all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                        transform: scale(1);
                        opacity: 1;
                      `;

                      document.body.appendChild(floatingElement);

                      setTimeout(() => {
                        const cartIcon = document.querySelector('nav a[href="/cart"] svg');
                        if (cartIcon) {
                          const cartRect = cartIcon.getBoundingClientRect();
                          floatingElement.style.left = (cartRect.left + cartRect.width / 2 - 15) + 'px';
                          floatingElement.style.top = (cartRect.top + cartRect.height / 2 - 15) + 'px';
                          floatingElement.style.transform = 'scale(0.3)';
                          floatingElement.style.opacity = '0';
                        }
                      }, 100);

                      setTimeout(() => {
                        if (document.body.contains(floatingElement)) {
                          document.body.removeChild(floatingElement);
                        }
                      }, 1300);

                      // Create success notification
                      const currentLang = i18n.language === 'cz' ? 'cs' : i18n.language;
                      const productName = selectedProduct.name[currentLang] || selectedProduct.name.cs;

                      const notification = document.createElement('div');
                      notification.innerHTML = `
                        <div style="
                          background: linear-gradient(135deg, #22c55e, #16a34a);
                          color: white;
                          padding: 12px 20px;
                          border-radius: 12px;
                          box-shadow: 0 8px 32px rgba(34, 197, 94, 0.3);
                          font-weight: 600;
                          font-size: 14px;
                          display: flex;
                          align-items: center;
                          gap: 8px;
                        ">
                          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          ${t('shop.addedToCart')}: 1x ${productName}
                        </div>
                      `;

                      notification.style.cssText = `
                        position: fixed;
                        top: 20px;
                        right: 20px;
                        z-index: 9999;
                        pointer-events: none;
                        transform: translateX(100%);
                        transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                      `;

                      document.body.appendChild(notification);

                      setTimeout(() => {
                        notification.style.transform = 'translateX(0)';
                      }, 200);

                      setTimeout(() => {
                        notification.style.transform = 'translateX(100%)';
                      }, 2500);

                      setTimeout(() => {
                        if (document.body.contains(notification)) {
                          document.body.removeChild(notification);
                        }
                      }, 3000);
                    } else {
                      closeProductDetails();
                    }
                  }}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-lg hover:from-blue-400 hover:to-blue-500 transition-all font-semibold"
                  disabled={selectedProduct.isSoldOut}
                >
                  {selectedProduct.isSoldOut ? t('shop.soldOut') : t('shop.addToCart')}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Edit Product Modal */}
      {showEditModal && editingProduct && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-gray-900 border border-blue-500/30 rounded-2xl p-8 w-[90vw] max-w-4xl max-h-[90vh] overflow-y-auto relative z-[60]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-white">{t('common.editProduct')}</h3>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <ProductEditForm
              product={editingProduct}
              categories={categories}
              themeColor="blue-500"
              onSave={handleEditProduct}
              onCancel={closeEditModal}
            />
          </motion.div>
        </motion.div>
      )}

      {/* Category Manager */}
      {showCategoryManager && (
        <CategoryManager
          categories={categories}
          onSave={(updatedCategories) => {
            handleSaveCategories(updatedCategories);
          }}
          onClose={() => setShowCategoryManager(false)}
          themeColor="#3B82F6"
          section="robotics"
          addCategory={addCategory}
          editCategory={editCategory}
          removeCategory={removeCategory}
        />
      )}

      {/* Sections Editor */}
      {showSectionsEditor && (
        <RoboticsSectionsEditor
          sections={roboticsSections}
          onSave={updateRoboticsSections}
          onClose={() => setShowSectionsEditor(false)}
        />
      )}

      {/* Inquiry Form */}
      {showInquiryForm && (
        <InquiryForm
          onClose={() => setShowInquiryForm(false)}
          section="Cybertronic Robotics"
        />
      )}
    </div>
  );
}


// Category Manager Component
interface CategoryManagerProps {
  categories: Category[];
  onSave: (categories: Category[]) => void;
  onClose: () => void;
  themeColor: string;
  section: string;
  addCategory: (data: { name: string; color: string }) => Promise<any>;
  editCategory: (id: string, data: { name: string; color: string }) => Promise<any>;
  removeCategory: (id: string) => Promise<void>;
}

function CategoryManager({ categories, onSave, onClose, themeColor, section, addCategory, editCategory, removeCategory }: CategoryManagerProps) {
  const { t } = useTranslation();
  const [editingCategories, setEditingCategories] = useState(categories);
  const [saving, setSaving] = useState(false);

  const handleAddCategory = () => {
    const newCategory: Category = {
      id: `temp_${Date.now()}`,
      name: '',
      color: themeColor
    };
    setEditingCategories([...editingCategories, newCategory]);
  };

  const updateCategory = (id: string, field: string, value: string) => {
    setEditingCategories(prev => 
      prev.map(cat => cat.id === id ? { ...cat, [field]: value } : cat)
    );
  };

  const handleDeleteCategory = (id: string) => {
    setEditingCategories(prev => prev.filter(cat => cat.id !== id));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Process each category
      for (const category of editingCategories) {
        if (category.name.trim() === '') continue;

        if (category.id.startsWith('temp_')) {
          // New category
          await addCategory({
            name: category.name,
            color: category.color
          });
        } else {
          // Existing category - only update if it changed
          const originalCategory = categories.find(cat => cat.id === category.id);
          if (originalCategory &&
              (originalCategory.name !== category.name ||
               originalCategory.color !== category.color)) {
            await editCategory(category.id, {
              name: category.name,
              color: category.color
            });
          }
        }
      }

      // Handle deleted categories
      const deletedCategories = categories.filter(
        cat => !editingCategories.find(editCat => editCat.id === cat.id)
      );

      for (const deletedCat of deletedCategories) {
        if (deletedCat.id && !deletedCat.id.startsWith('temp_')) {
          await removeCategory(deletedCat.id);
        }
      }

      onClose();
    } catch (err) {
      console.error('Error saving categories:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-900 border border-blue-500/30 rounded-2xl p-8 w-full max-w-4xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <h3 className="text-2xl font-bold text-white">{t('common.categoryManagement')}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4 mb-6">
          {editingCategories.map((category) => (
            <div key={category.id} className="flex items-center space-x-4 bg-gray-800 p-4 rounded-lg">
              <input
                type="text"
                value={category.name}
                onChange={(e) => updateCategory(category.id, 'name', e.target.value)}
                placeholder={t('common.categoryName')}
                className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              />
              <input
                type="color"
                value={category.color}
                onChange={(e) => updateCategory(category.id, 'color', e.target.value)}
                className="w-12 h-10 rounded border border-gray-600"
              />
              <button
                onClick={() => handleDeleteCategory(category.id)}
                className="text-red-400 hover:text-red-300 p-2"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex space-x-4">
          <button
            onClick={handleAddCategory}
            className="bg-green-600 hover:bg-green-500 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            {t('common.addCategory')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-600 transition-colors font-semibold"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-lg hover:from-blue-400 hover:to-blue-500 transition-all font-semibold"
          >
            {saving ? 'Ukládání...' : 'Uložit změny'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}